import { Injectable } from '@angular/core';
import {
  Auth,
  User,
  UserCredential,
  authState,
  getAuth,
  idToken,
  isSignInWithEmailLink,
  onAuthStateChanged,
  signInWithCustomToken,
  signInWithEmailLink,
  signOut,
} from '@angular/fire/auth';
import { initializeApp } from 'firebase/app';
import { Persistence, browserSessionPersistence, getIdToken, inMemoryPersistence, setPersistence } from '@firebase/auth';
import { Observable, firstValueFrom } from 'rxjs';

@Injectable()
export class FirebaseWrapperService {
  constructor(private fireAuth: Auth) {}

  authStateChanged(): Observable<User> {
    return new Observable((subscriber) => {
      return onAuthStateChanged(
        this.fireAuth,
        subscriber,
        (err) => subscriber.error(err),
        () => subscriber.complete(),
      );
    });
  }

  signOut(): Promise<void> {
    return signOut(this.fireAuth);
  }

  idToken(): Observable<string> {
    return idToken(this.fireAuth);
  }

  async refreshTokenAsync(): Promise<string> {
    const user = await firstValueFrom(this.authState());
    return getIdToken(user, true);
  }

  isSignInWithEmailLink(link: string): boolean {
    return isSignInWithEmailLink(this.fireAuth, link);
  }

  changeApp(appName: string): void {
    const firebaseApp = initializeApp(window['__AnPlatformConfiguration__'].firebase, appName);

    this.fireAuth = getAuth(firebaseApp);
  }

  async signInWithEmailLinkAsync(email: string, link?: string, persistent = true): Promise<UserCredential> {
    const auth = this.fireAuth;

    if (persistent === false) {
      // Please refer to for further explanation: https://firebase.google.com/docs/auth/web/auth-state-persistence
      auth.setPersistence({ type: 'SESSION' });
    }

    const user = link
      ? signInWithEmailLink(this.fireAuth, email, link)
      : signInWithEmailLink(this.fireAuth, email, window.location.href);
    return user;
  }

  authState(): Observable<User> {
    return authState(this.fireAuth);
  }

  setTenantId(tenantId: string): void {
    this.fireAuth.tenantId = tenantId;
  }

  getCurrentUser(): User {
    return this.fireAuth.currentUser;
  }

  async signInWithToken(token: string, persistence: Persistence = null): Promise<UserCredential> {
    const auth = this.fireAuth;
    if (persistence === inMemoryPersistence || persistence === browserSessionPersistence) {
      // Please refer to for further explanation: https://firebase.google.com/docs/auth/web/auth-state-persistence
      // We selected inMemoryPersistence which is basically means no persistence between tabs to support external approval
      // links functionality where we want to authenticate for every link we open to support parallel approvals for different
      // approval links
      // *UPDATE*: added ability to use browserSessionPersistence type to keep user signed in even after page refresh, but until tab is closed
      await setPersistence(auth, persistence);
    }
    return signInWithCustomToken(this.fireAuth, token);
  }
}
